import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { opApi, setCsrf, apiError } from "@/lib/api";
import { hostBrand, brandPath, applyBrandTitle } from "@/lib/brand";

const OperatorContext = createContext(null);
export const useOperator = () => useContext(OperatorContext);

// No access: either a paywall redirect from MorrAccess, or a plain message (no app access / seats full).
function denied(data) {
  if (data.redirect_url) { window.location.href = data.redirect_url; return { redirected: true }; }
  throw new Error(data.message || "You don't have access to this app.");
}

export function OperatorProvider({ children }) {
  const [operator, setOperator] = useState(null); // null=loading, false=unauth, object=auth
  const [redirectUrl, setRedirectUrl] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await opApi.get("/operator/me");
      setCsrf(data.csrf_token);
      // Signed-in product decides the address: a MorrConnect session on /dashboard moves to /connect/dashboard.
      const { pathname, search, hash } = window.location;
      if ((data.app === "morrconnect") !== (hostBrand().key === "morrconnect") && /^(\/connect)?\/(dashboard|admin)$/.test(pathname)) {
        const bare = pathname.replace(/^\/connect/, "");
        window.history.replaceState({}, "", `${brandPath(bare, data.app)}${search}${hash}`);
        applyBrandTitle();
      }
      setOperator(data);
      return data;
    } catch (e) {
      if (e.response?.status === 402) {
        setRedirectUrl(e.response.data?.detail?.redirect_url);
      }
      setOperator(false);
      return false;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await opApi.post("/operator/login", { email, password, app: hostBrand().key });
    if (!data.access) return denied(data);
    setCsrf(data.csrf_token);
    const me = await refresh();
    return { role: me.role, tenant_id: me.tenant_id };
  }, [refresh]);

  const launch = useCallback(async (lt) => {
    const { data } = await opApi.post("/operator/launch", { lt, app: hostBrand().key });
    if (!data.access) return denied(data);
    setCsrf(data.csrf_token);
    const me = await refresh();
    return { role: me.role, tenant_id: me.tenant_id };
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await opApi.post("/operator/logout"); } catch (e) {}
    setCsrf(null);
    setOperator(false);
  }, []);

  const impersonate = useCallback(async (tenantId) => {
    await opApi.post(`/operator/impersonate/${tenantId}`);
    await refresh();
  }, [refresh]);

  const stopImpersonate = useCallback(async () => {
    await opApi.post("/operator/stop-impersonate");
    await refresh();
  }, [refresh]);

  return (
    <OperatorContext.Provider value={{ operator, redirectUrl, refresh, login, launch, logout, impersonate, stopImpersonate, apiError }}>
      {children}
    </OperatorContext.Provider>
  );
}
